import React from 'react'
import styled from 'styled-components'
import {
  addMinutes,
  startOfDay,
  isSameDay,
  format,
  parse,
  isValid,
} from 'date-fns'
import { toast } from 'react-toastify'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { BusinessInfoInterface } from 'src/client/interfaces/Common'
import { StyledForm } from 'src/components/Settings/common/layout'
import { StyledContent } from 'src/components/Settings/Business/styled'
import ButtonForm from 'src/components/Settings/Business/ButtonForm'
import { MutationBusinessInfoProps } from 'src/containers/Settings/Business'
import Select from 'src/stories/Select'
import { UnknownObject } from 'src/utils/interfaces'
import HelperTooltip, { HelperTooltipWrapper } from 'src/stories/HelperTooltip'
import { useLocationContext } from 'src/contexts/LocationContext'
import { OperatingHour, OperatingHours } from 'src/client/interfaces/Locations'
import { Body, Heading } from 'src/stories/typography'
import Checkbox from 'src/stories/Checkbox'

const StyledContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.space(4),
  maxWidth: theme.space(90),
}))

const StyledSelectContainer = styled.div({
  display: 'flex',
  alignItems: 'start',
})

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

type DaysOfWeek = (typeof DAYS_OF_WEEK)[number]

const PERIOD_HOUR = {
  CLOSED: 'Closed',
  OPEN_24_HOURS: 'Open 24 Hours',
  OPEN_24_HOURS_FROM_TIME: '12:00am',
  OPEN_24_HOURS_TO_TIME: '11:59pm',
}

const isOpen24Hours = (day: OperatingHour) =>
  day.from === PERIOD_HOUR.OPEN_24_HOURS_FROM_TIME &&
  day.to === PERIOD_HOUR.OPEN_24_HOURS_TO_TIME

const isPeriodHour = (value: string) =>
  [PERIOD_HOUR.CLOSED, PERIOD_HOUR.OPEN_24_HOURS].includes(value)

const UI_DATE_FORMAT = 'h:mm aaa'
const BACKEND_DATE_FORMAT = 'h:mmaaa'

export const displayHoursFormatter = (businessHours: OperatingHours) =>
  DAYS_OF_WEEK.map((day, index) => {
    const data = businessHours[index]

    const formattedDay = {
      dayOfWeek: day,
      label: PERIOD_HOUR.CLOSED,
    }

    if (isOpen24Hours(data)) {
      formattedDay.label = PERIOD_HOUR.OPEN_24_HOURS

      return formattedDay
    }

    if (!data.isInvalid) {
      const from = parse(data.from, BACKEND_DATE_FORMAT, new Date())
      const to = parse(data.to, BACKEND_DATE_FORMAT, new Date())

      if (isValid(from) && isValid(to)) {
        const fromFormatted = format(from, UI_DATE_FORMAT)
        const toFormatted = format(to, UI_DATE_FORMAT)

        formattedDay.label = `${fromFormatted} - ${toFormatted}`
      }
    }

    return formattedDay
  })

const validHoursIntervalOptions = () => {
  const validHours = [
    {
      label: PERIOD_HOUR.CLOSED,
      value: PERIOD_HOUR.CLOSED,
    },
    {
      label: PERIOD_HOUR.OPEN_24_HOURS,
      value: PERIOD_HOUR.OPEN_24_HOURS,
    },
  ]
  const today = new Date()
  let date = startOfDay(today)

  while (isSameDay(today, date)) {
    validHours.push({
      label: format(date, UI_DATE_FORMAT),
      value: format(date, BACKEND_DATE_FORMAT), // Format expected by core 'HH:MMam'
    })
    date = addMinutes(date, 30)
  }

  return validHours
}

const hoursFormSchema = yup.object({
  publishHours: yup.boolean().default(false),
  daysOfWeek: yup
    .array()
    .length(7)
    .of(
      yup.object({
        from: yup
          .string()
          .default('')
          .test(
            'isSameTime',
            'Open time cannot match close time.',
            (value, context) =>
              isPeriodHour(value)
                ? true
                : value !== (context.parent as DayHours).to
          ),
        to: yup.string().default(''),
      })
    ),
})

type DayHours = Pick<OperatingHour, 'from' | 'to'>
interface HoursFromSchema {
  publishHours: boolean
  daysOfWeek: [
    DayHours,
    DayHours,
    DayHours,
    DayHours,
    DayHours,
    DayHours,
    DayHours
  ]
}

interface HoursFormProps extends MutationBusinessInfoProps {
  baseDataAttribute: string
}

const HoursForm: React.FC<HoursFormProps> = ({
  baseDataAttribute,
  isLoading,
  update,
  onSubmitFinished,
}) => {
  const { activeLocationV3 } = useLocationContext()
  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
    control,
    setError,
  } = useForm<HoursFromSchema>({
    resolver: yupResolver(hoursFormSchema),
    defaultValues: {
      publishHours: activeLocationV3.businessInfo.publishHours,
      daysOfWeek: activeLocationV3.businessInfo.operatingHours.map((hour) => {
        if (isOpen24Hours(hour)) {
          return {
            from: PERIOD_HOUR.OPEN_24_HOURS,
            to: PERIOD_HOUR.OPEN_24_HOURS,
          }
        }

        if (hour.isClosed || hour.isInvalid) {
          return {
            from: PERIOD_HOUR.CLOSED,
            to: PERIOD_HOUR.CLOSED,
          }
        }

        return {
          from: hour.from,
          to: hour.to,
        }
      }) as HoursFromSchema['daysOfWeek'],
    },
  })

  const onSubmit = handleSubmit(async (data) => {
    try {
      const hours = data.daysOfWeek.map(
        (dayOfWeek): BusinessInfoInterface['hours'][number] => ({
          from:
            dayOfWeek.from === PERIOD_HOUR.OPEN_24_HOURS
              ? PERIOD_HOUR.OPEN_24_HOURS_FROM_TIME
              : dayOfWeek.from,
          to:
            dayOfWeek.to === PERIOD_HOUR.OPEN_24_HOURS
              ? PERIOD_HOUR.OPEN_24_HOURS_TO_TIME
              : dayOfWeek.to,
        })
      ) as HoursFromSchema['daysOfWeek']

      await update({
        hours,
        publishHours: data.publishHours,
      })

      onSubmitFinished()
    } catch (error: unknown) {
      const response = (error as UnknownObject | undefined)?.data as
        | ({ [path: string]: string } & { propertyName: string[] })
        | undefined

      if (response?.propertyName && response.propertyName.length > 0) {
        response?.propertyName?.forEach((propertyPath) => {
          const match = propertyPath.match(/[0-6]/)
          const inputIndex = match !== null ? Number(match[0]) : 0
          const target = propertyPath.match(/from|to/)![0] as 'from' | 'to'
          const fieldName = `daysOfWeek.${inputIndex}.${target}`

          if (response[propertyPath])
            setError(fieldName as 'daysOfWeek.0', {
              type: 'server',
              message: response[propertyPath],
            })
        })
      } else {
        toast.error(
          'There was an error trying to save, please review the information and try again'
        )
      }
    }
  })

  const disableForm = isSubmitting || isLoading

  const selectProps = {
    verticallySpace: 4,
    disabled: disableForm,
    options: validHoursIntervalOptions(),
  }

  return (
    <StyledForm data-cy={`${baseDataAttribute}-form-hours`} onSubmit={onSubmit}>
      <StyledContent>
        <div>
          <Heading as="h3">Hours</Heading>
          <Body>Select hours of operation for your business.</Body>
        </div>
        <StyledContainer data-cy={`${baseDataAttribute}-form-hours`}>
          {DAYS_OF_WEEK.map((day: DaysOfWeek, idx: number) => {
            return (
              <StyledSelectContainer key={day}>
                <Controller
                  name={`daysOfWeek.${idx}` as 'daysOfWeek.0'}
                  control={control}
                  render={({
                    field,
                    fieldState: { isTouched, isDirty, error },
                    formState: { errors },
                  }) => {
                    return (
                      <>
                        <Select
                          {...selectProps}
                          label={`${day}:`}
                          initialValue={field.value.from}
                          errorText={errors.daysOfWeek?.[idx]?.from?.message}
                          onChange={(value) => {
                            field.onChange({
                              from: value,
                              to:
                                isPeriodHour(value) ||
                                isPeriodHour(field.value.to)
                                  ? value
                                  : field.value.to,
                            })
                          }}
                          containerStyle={{
                            width: '50%',
                          }}
                          inputStyle={
                            isPeriodHour(field.value.from)
                              ? undefined
                              : {
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                  marginRight: 0,
                                }
                          }
                        />
                        <Select
                          {...selectProps}
                          label={`${day}:`}
                          initialValue={field.value.to}
                          errorText={errors.daysOfWeek?.[idx]?.to?.message}
                          onChange={(value) => {
                            field.onChange({
                              from: isPeriodHour(value)
                                ? value
                                : field.value.from,
                              to: value,
                            })
                          }}
                          hideLabel
                          inputStyle={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            marginLeft: 0,
                          }}
                          containerStyle={{
                            width: '50%',
                            visibility: isPeriodHour(field.value.from)
                              ? 'hidden'
                              : undefined,
                          }}
                        />
                      </>
                    )
                  }}
                />
              </StyledSelectContainer>
            )
          })}
          <HelperTooltipWrapper>
            <Checkbox
              {...selectProps}
              label="Show my business hours to contacts"
              defaultChecked={activeLocationV3.businessInfo.publishHours}
              {...register('publishHours')}
            />
            <HelperTooltip>
              Checking this option displays your hours of operation in your
              Testimonial page.
            </HelperTooltip>
          </HelperTooltipWrapper>
        </StyledContainer>
        <ButtonForm
          baseDataAttribute={`${baseDataAttribute}-form-hours`}
          disabled={disableForm}
        />
      </StyledContent>
    </StyledForm>
  )
}

export default HoursForm
